import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Grid,
  Stack,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowForward as ArrowForwardIcon,
  Chat as ChatIcon,
  FormatListBulleted as BulletsIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Card, Pill, SectionHeader } from '../shared';
import ProvideContextModal from './ProvideContextModal';
import { SAGE, CREAM, WARM } from '../../theme';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';

const STATUS_PRESETS = {
  draft: 'neutral',
  conversed: 'warm',
  detailed: 'warm',
  polished: 'sage',
};

const ExperienceTabV2 = ({ experience = [], onUpdate, onDirtyChange }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [localExperience, setLocalExperience] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(null);

  // Add project dialog
  // The accomplishment currently being talked about, or null.
  const [contextTarget, setContextTarget] = useState(null);
  const [addProjectDialog, setAddProjectDialog] = useState({ open: false, expIndex: null });
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  useEffect(() => {
    setLocalExperience([...experience]);
  }, [experience]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => setIsEditing(true);
  const handleSave = () => {
    onUpdate(localExperience);
    setIsEditing(false);
    setIsDirty(false);
  };
  const handleCancel = () => {
    setLocalExperience([...experience]);
    setIsEditing(false);
    setIsDirty(false);
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleAddNew = () => {
    setEditForm({
      company: '', title: '', location: '', start_date: '', end_date: '',
      accomplishments: [], projects: [], tags: [],
    });
    setEditingIndex(localExperience.length);
  };

  const handleEditItem = (index) => {
    setEditForm({ ...localExperience[index] });
    setEditingIndex(index);
  };

  const handleSaveItem = () => {
    const updated = [...localExperience];
    if (editingIndex < updated.length) {
      updated[editingIndex] = editForm;
    } else {
      updated.push(editForm);
    }
    setLocalExperience(updated);
    setEditingIndex(null);
    setEditForm(null);
    setIsDirty(true);
  };

  const handleCancelItem = () => { setEditingIndex(null); setEditForm(null); };

  const handleDeleteItem = (index) => {
    if (window.confirm('Delete this experience?')) {
      setLocalExperience(localExperience.filter((_, i) => i !== index));
      setIsDirty(true);
    }
  };

  const handleAddAccomplishment = () => {
    setEditForm({ ...editForm, accomplishments: [...editForm.accomplishments, { text: '', tags: [] }] });
  };
  const handleUpdateAccomplishment = (accIndex, field, value) => {
    const updated = [...editForm.accomplishments];
    updated[accIndex] = { ...updated[accIndex], [field]: value };
    setEditForm({ ...editForm, accomplishments: updated });
  };
  const handleDeleteAccomplishment = (accIndex) => {
    setEditForm({ ...editForm, accomplishments: editForm.accomplishments.filter((_, i) => i !== accIndex) });
  };

  // Add project via API
  const handleAddProject = async () => {
    if (!newProjectTitle.trim()) return;
    setCreatingProject(true);
    try {
      const response = await fetch(
        `${API_URL}/api/profile/experience/${addProjectDialog.expIndex}/projects`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...mockAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ title: newProjectTitle.trim(), description: newProjectDesc.trim() }),
        }
      );
      if (response.ok) {
        const project = await response.json();
        // Update local state to show the new project immediately
        const updated = [...localExperience];
        if (!updated[addProjectDialog.expIndex].projects) {
          updated[addProjectDialog.expIndex].projects = [];
        }
        updated[addProjectDialog.expIndex].projects.push(project);
        setLocalExperience(updated);
        // Also push up to parent so profile state is consistent
        onUpdate(updated);
        setAddProjectDialog({ open: false, expIndex: null });
        setNewProjectTitle('');
        setNewProjectDesc('');
      } else {
        alert('Failed to create project.');
      }
    } catch (err) {
      console.error('[EXP] Error creating project:', err);
      alert('Error creating project.');
    } finally {
      setCreatingProject(false);
    }
  };

  // Render edit form
  const renderItemForm = () => (
    <Card sx={{ mb: 2, p: 3, bgcolor: CREAM[200] }}>
      <SectionHeader sx={{ mb: 2 }}>
        {editingIndex < localExperience.length ? 'Edit Experience' : 'Add Experience'}
      </SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Company" value={editForm.company}
            onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
            size="small" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Job Title" value={editForm.title}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
            size="small" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Location" value={editForm.location}
            onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
            size="small" />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField fullWidth label="Start Date" placeholder="Jan 2020" value={editForm.start_date}
            onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })}
            size="small" />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField fullWidth label="End Date" placeholder="Present" value={editForm.end_date}
            onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })}
            size="small" />
        </Grid>
      </Grid>

      <SectionHeader sx={{ mt: 3, mb: 1 }}>Accomplishments</SectionHeader>
      {editForm.accomplishments.map((acc, index) => (
        <Box key={index} sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField fullWidth multiline rows={2} size="small"
            label={`Accomplishment ${index + 1}`} value={acc.text}
            onChange={(e) => handleUpdateAccomplishment(index, 'text', e.target.value)} />
          <IconButton onClick={() => handleDeleteAccomplishment(index)} size="small" sx={{ color: WARM.rose, mt: 1 }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button startIcon={<AddIcon />} onClick={handleAddAccomplishment} size="small"
        sx={{ color: SAGE[500], textTransform: 'none', fontFamily: "'Outfit', sans-serif" }}>
        Add Accomplishment
      </Button>

      <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={handleCancelItem} size="small"
          sx={{ color: SAGE[500], textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleSaveItem} variant="contained" size="small"
          sx={{ bgcolor: SAGE[500], textTransform: 'none', '&:hover': { bgcolor: SAGE[600] } }}>
          Save
        </Button>
      </Box>
    </Card>
  );

  // Render project card
  const renderProjectCard = (proj) => {
    const bulletCount = proj.bullets?.length || 0;
    const convCount = proj.conversation_ids?.length || 0;
    return (
      <Box
        key={proj.id}
        onClick={() => navigate(`/projects/${proj.id}`)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${SAGE[100]}`,
          bgcolor: CREAM[50],
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          '&:hover': { borderColor: SAGE[300], bgcolor: SAGE[50] },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{
              fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.85rem', color: SAGE[800],
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {proj.title}
            </Typography>
            <Pill preset={STATUS_PRESETS[proj.status] || 'neutral'} label={proj.status || 'draft'} />
          </Box>
          {proj.description && (
            <Typography variant="body2" sx={{
              color: SAGE[500], fontSize: '0.75rem',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {proj.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
            {convCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <ChatIcon sx={{ fontSize: 12, color: SAGE[400] }} />
                <Typography sx={{ fontSize: '0.65rem', color: SAGE[400] }}>{convCount}</Typography>
              </Box>
            )}
            {bulletCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <BulletsIcon sx={{ fontSize: 12, color: SAGE[400] }} />
                <Typography sx={{ fontSize: '0.65rem', color: SAGE[400] }}>{bulletCount}</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <ArrowForwardIcon sx={{ fontSize: 16, color: SAGE[300], flexShrink: 0 }} />
      </Box>
    );
  };

  // Render experience card in view mode
  const renderViewItem = (exp, index) => {
    const projects = exp.projects || [];
    return (
      <Card key={index} sx={{ mb: 2, p: 0 }}>
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1rem', color: SAGE[800] }}>
                {exp.title}
              </Typography>
              <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '0.9rem', color: SAGE[600] }}>
                {exp.company}
              </Typography>
              <Typography variant="body2" sx={{ color: SAGE[400], fontSize: '0.8rem' }}>
                {exp.location} {exp.start_date && `\u2022 ${exp.start_date} \u2013 ${exp.end_date || 'Present'}`}
              </Typography>
            </Box>
            {isEditing && (
              <Stack direction="row" spacing={0.5}>
                <IconButton onClick={() => handleEditItem(index)} size="small" sx={{ color: SAGE[400] }}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => handleDeleteItem(index)} size="small" sx={{ color: WARM.rose }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}
          </Box>

          {/* Accomplishments — bullet plus the story behind it.
              The bullet is the claim; context is what someone gets when they
              want to know whether the claim means anything. Written by talking,
              not typing, and only ever published after being condensed against
              a specific requirement. */}
          {exp.accomplishments?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <SectionHeader sx={{ mb: 0.5 }}>Accomplishments</SectionHeader>
              {exp.accomplishments.map((acc, i) => (
                <Box
                  key={acc.id || i}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                    mb: 0.3, pl: 1, '&:hover .ctx-action': { opacity: 1 },
                  }}
                >
                  <Typography variant="body2" sx={{ color: SAGE[700], fontSize: '0.8rem', flex: 1 }}>
                    {acc.text}
                  </Typography>
                  {acc.context && (
                    <Tooltip title="Has context">
                      <NotesIcon sx={{ fontSize: 14, color: SAGE[400], mt: '2px' }} />
                    </Tooltip>
                  )}
                  {acc.id && (
                    <Button
                      className="ctx-action"
                      size="small"
                      onClick={() => setContextTarget({ ...acc, company: exp.company })}
                      sx={{
                        opacity: acc.context ? 0.6 : 0, transition: 'opacity 120ms',
                        textTransform: 'none', fontFamily: "'Outfit', sans-serif",
                        fontSize: '0.7rem', color: SAGE[500], minWidth: 0, py: 0, px: 0.75,
                        flexShrink: 0,
                      }}
                    >
                      {acc.context ? 'Revisit' : 'Provide context'}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Projects */}
          {(projects.length > 0 || true) && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <SectionHeader>
                  Projects {projects.length > 0 && `(${projects.length})`}
                </SectionHeader>
              </Box>
              {projects.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {projects.map(renderProjectCard)}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: SAGE[400], fontSize: '0.75rem', fontStyle: 'italic' }}>
                  No projects yet
                </Typography>
              )}
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddProjectDialog({ open: true, expIndex: index })}
                size="small"
                sx={{
                  mt: 1,
                  color: SAGE[500],
                  textTransform: 'none',
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: '0.75rem',
                  borderStyle: 'dashed',
                  borderColor: SAGE[200],
                  '&:hover': { borderColor: SAGE[400], bgcolor: SAGE[50] },
                }}
                variant="outlined"
              >
                Add Project
              </Button>
            </Box>
          )}
        </Box>
      </Card>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, fontSize: '1.1rem', color: SAGE[900] }}>
          Work Experience
        </Typography>
        {!isEditing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit} size="small"
            sx={{ textTransform: 'none', fontFamily: "'Outfit', sans-serif", borderColor: SAGE[300], color: SAGE[600] }}>
            Edit
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button onClick={handleCancel} size="small"
              sx={{ textTransform: 'none', color: SAGE[500] }}>Cancel</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}
              disabled={!isDirty} size="small"
              sx={{ textTransform: 'none', bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Box>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
          You have unsaved changes.
        </Alert>
      )}

      {isEditing && editingIndex === null && (
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddNew} size="small"
          sx={{ mb: 2, textTransform: 'none', fontFamily: "'Outfit', sans-serif", borderColor: SAGE[300], color: SAGE[600] }}>
          Add Experience
        </Button>
      )}

      {editingIndex !== null && editForm && renderItemForm()}

      {localExperience.length === 0 ? (
        <Typography variant="body2" sx={{ color: SAGE[400], textAlign: 'center', py: 4 }}>
          No work experience added yet.
          {isEditing && ' Click "Add Experience" to get started.'}
        </Typography>
      ) : (
        localExperience.map((exp, index) =>
          editingIndex === index ? null : renderViewItem(exp, index)
        )
      )}

      {/* Add Project Dialog */}
      <Dialog
        open={addProjectDialog.open}
        onClose={() => !creatingProject && setAddProjectDialog({ open: false, expIndex: null })}
        maxWidth="sm" fullWidth
      >
        <DialogTitle sx={{ fontFamily: "'Outfit', sans-serif" }}>Add Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Project Title" value={newProjectTitle}
            onChange={(e) => setNewProjectTitle(e.target.value)}
            disabled={creatingProject} sx={{ mt: 1, mb: 2 }}
            placeholder="e.g., Data Pipeline Redesign"
          />
          <TextField
            fullWidth multiline rows={2} label="Description (optional)"
            value={newProjectDesc} onChange={(e) => setNewProjectDesc(e.target.value)}
            disabled={creatingProject}
            placeholder="Brief description of the project"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProjectDialog({ open: false, expIndex: null })}
            disabled={creatingProject} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleAddProject} variant="contained"
            disabled={!newProjectTitle.trim() || creatingProject}
            sx={{ textTransform: 'none', bgcolor: SAGE[500], '&:hover': { bgcolor: SAGE[600] } }}>
            {creatingProject ? 'Creating...' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      <ProvideContextModal
        open={Boolean(contextTarget)}
        accomplishment={contextTarget}
        onClose={() => setContextTarget(null)}
        onSaved={(result) => {
          // Reflect the new context locally so the row updates without a refetch.
          setLocalExperience((prev) => prev.map((exp) => ({
            ...exp,
            accomplishments: (exp.accomplishments || []).map((a) =>
              a.id === result.accomplishment_id
                ? { ...a, context: result.context,
                    context_updated_at: result.context_updated_at }
                : a),
          })));
        }}
      />
    </Box>
  );
};

export default ExperienceTabV2;
